/**
 * Rules of the Made-to-Measure order details: parts grouped by sewing pattern,
 * what a role may edit per status, the measurements, the timeline of a part and
 * the PDF names.
 */
import { DMS_PRODUCTLINE, ROLES, type Role } from '@/shared/constants';
import { getImage, STORAGE, type StorageName } from '@/shared/lib/image';
import { sortByKey } from '@/shared/lib/sort';
import type { Measurement, Order, OrderPart } from '../api';
import {
  ABC_ORDER_TYPES,
  compareStatus,
  CS,
  ORDER_TYPES,
  STATUS_COLORS,
} from '../order-types';
import { formatLocalDate } from '../logic';

/**
 * `ORDER_ID_IS_REQUIRED`, false on every environment: kept as a constant so
 * the rules read like the historical ones.
 */
export const ORDER_ID_IS_REQUIRED = false;

export type {
  Measurement,
  OrderPart,
  PartModelData,
  PartSelected,
} from '../api';

export const orderParts = (order: Order): OrderPart[] => order.parts ?? [];

const intersects = (a: readonly string[], b: readonly string[]) =>
  a.some((value) => b.includes(value));

/** The sewing pattern names of a part's model (the referential objects, since PCRS-9758). */
const sewingPatternNames = (part: OrderPart): string[] =>
  (part.model.data.sewingPatternObjects ?? []).map(({ name }) => name);

/**
 * The sewing pattern a part is printed under. Pants
 * with several patterns follow the suit or jacket ordered with them.
 */
export const effectiveSewingPattern = (
  part: OrderPart,
  parts: readonly OrderPart[],
): string | undefined => {
  const patterns = sewingPatternNames(part);
  if (
    patterns.length <= 1 ||
    part.model.data.productLine !== DMS_PRODUCTLINE.PANTS
  )
    return patterns[0];
  const suit = parts.find(
    (other) =>
      other.model.data.productLine === DMS_PRODUCTLINE.SUIT &&
      other.model.data.pantsModel === part.model.data.model,
  );
  const jacket = parts.find(
    (other) =>
      other.model.data.productLine === DMS_PRODUCTLINE.JACKET &&
      intersects(sewingPatternNames(other), patterns),
  );
  const matching = suit ?? jacket;
  return matching ? sewingPatternNames(matching)[0] : patterns[0];
};

export interface GroupedParts {
  /** One PDF for every shirt. */
  shirts: OrderPart[];
  /** The other parts, one group per sewing pattern. */
  othersByPattern: Record<string, OrderPart[]>;
  /** Every non-shirt part, in pattern order (the rows of the table). */
  others: OrderPart[];
}
export const groupParts = (parts: readonly OrderPart[]): GroupedParts => {
  const shirts: OrderPart[] = [];
  const othersByPattern: Record<string, OrderPart[]> = {};
  for (const part of parts) {
    if (part.model.data.productLine === DMS_PRODUCTLINE.SHIRT) {
      shirts.push(part);
      continue;
    }
    const pattern = String(effectiveSewingPattern(part, parts));
    const group = (othersByPattern[pattern] ??= []);
    group.push(part);
  }
  return {
    shirts,
    othersByPattern,
    others: Object.values(othersByPattern).flat(),
  };
};

export const partGroups = (grouped: GroupedParts): OrderPart[][] =>
  [grouped.others, grouped.shirts].filter((group) => group.length > 0);

export const withoutCancelled = (parts: readonly OrderPart[]): OrderPart[] =>
  parts.filter((part) => part.customisationStatusId !== CS.CANCELLED);

const isShirt = (part: OrderPart) =>
  part.model.data.productLine === DMS_PRODUCTLINE.SHIRT;

const uniqueMeasureTypes = (parts: readonly OrderPart[]) => [
  ...new Set(parts.map((part) => String(part.selected.measureType ?? ''))),
];
export const shirtMeasureTypes = (parts: readonly OrderPart[]) =>
  uniqueMeasureTypes(parts.filter(isShirt));
export const nonShirtMeasureTypes = (parts: readonly OrderPart[]) =>
  uniqueMeasureTypes(parts.filter((part) => !isShirt(part)));

/** The checkboxes shown in the details. */
export const dmsValidationFields = (parts: readonly OrderPart[]): string[] => {
  const fields = ['dmsPriceGeneration', 'dmsPurchaseOrder'];
  if (nonShirtMeasureTypes(parts).length > 0) fields.unshift('dmsSentToCaruso');
  if (shirtMeasureTypes(parts).length > 0) fields.unshift('dmsSentToGherardi');
  return fields;
};

export type DmsField =
  | 'externalOrderId'
  | 'externalClientId'
  | 'deliveryStore'
  | 'partSku'
  | 'partPrice'
  | 'partLocalPrice'
  | 'partFabric'
  | 'partSize'
  | 'partCount'
  | 'partSupplierExpectedDeliveryDate'
  | 'partQualityIssue'
  | 'partOaCode'
  | 'measurements'
  | 'any'
  | `product.${string}`;

const OPEN_STATUSES: readonly number[] = [
  CS.PENDING,
  CS.CONFIRMED,
  CS.READY_FOR_PRODUCTION,
  CS.IN_PROGRESS,
];
const PART_PENDING_FIELDS: readonly string[] = [
  'partSku',
  'partPrice',
  'partLocalPrice',
  'partFabric',
  'partSize',
  'partCount',
];
const PRODUCT_FLAGS: readonly string[] = [
  'product.dmsPriceGeneration',
  'product.dmsPurchaseOrder',
  'product.dmsSentToCaruso',
  'product.dmsSentToGherardi',
];

/**
 * What a field allows, from the status of the
 * part (or of the order); nothing while a part status is being saved.
 */
export const isDmsEditable = (
  role: Role,
  order: Order,
  field: DmsField | undefined,
  part: OrderPart | undefined,
  updatingParts: Readonly<Record<string, boolean>>,
): boolean => {
  if (orderParts(order).some((item) => updatingParts[item.id])) return false;
  const status = (part ?? order).customisationStatusId;
  if (status == null) return false;
  if (field === 'partSku' && role === ROLES.ADMIN)
    return OPEN_STATUSES.includes(status);
  if (field && PART_PENDING_FIELDS.includes(field))
    return status === CS.PENDING || status === CS.CONFIRMED;
  if (field === 'deliveryStore' || field === 'partSupplierExpectedDeliveryDate')
    return OPEN_STATUSES.includes(status);
  if (field === 'partQualityIssue') return status === CS.COMPLETED;
  if (field === 'partOaCode') return status === CS.READY_FOR_PRODUCTION;
  if (field && PRODUCT_FLAGS.includes(field)) return true;
  if (field === 'any') return status in ABC_ORDER_TYPES;
  return status === CS.PENDING;
};
export const isDmsAdmin = (role: Role): boolean => role === ROLES.ADMIN;
export const partStatusChangeAllowed = (
  order: Order,
  part: OrderPart,
  target: number,
  isEditMode: boolean,
): boolean => {
  const hasOrderId = Boolean(order.externalOrderId);
  if (isEditMode || (!hasOrderId && target !== CS.CANCELLED))
    return !ORDER_ID_IS_REQUIRED;
  if (part.customisationStatusId === CS.PENDING && target === CS.CONFIRMED)
    return ORDER_ID_IS_REQUIRED ? true : hasOrderId;
  return true;
};

export const partsStatusChangeAllowed = (
  order: Order,
  target: number,
): boolean =>
  orderParts(order).every((part) =>
    partStatusChangeAllowed(order, part, target, false),
  );

/** The service the order was taken in. */
export const dmsServiceLabel = (order: Order): string => {
  const event = (order.props?.event ?? null) as {
    id?: unknown;
    type?: string | null;
  } | null;
  if (!event?.id) return 'BOUTIQUE';
  return event.type?.toUpperCase() === 'VILLA DIOR' ? 'VILLA DIOR' : 'EVENT';
};

const lastLogStatus = (part: OrderPart): number => {
  const log = part.statusLogs?.at(-1);
  const status = Number(log?.currentStatusId ?? 0);
  return status === CS.READY_FOR_PRODUCTION ? 2.5 : status;
};

/** The part whose logs are shown on the right. */
export const lessAdvancedPart = (
  parts: readonly OrderPart[],
): OrderPart | undefined =>
  parts.reduce<OrderPart | undefined>(
    (least, part) =>
      least === undefined || lastLogStatus(part) < lastLogStatus(least)
        ? part
        : least,
    undefined,
  );

export interface TimelineItem {
  top: string;
  topColor: string;
  bottom: string;
  active?: boolean;
  time?: number;
}

const INACTIVE_COLOR = '#a9a9a9';

/** The logs, the Blois dates, then the statuses still to come. */
export const partTimelineItems = (
  part: OrderPart,
  order: Order,
): TimelineItem[] => {
  const items: TimelineItem[] = (part.statusLogs ?? []).map((log) => {
    const status = Number(log.currentStatusId ?? 0);
    const when = log.createdAt ? new Date(log.createdAt) : new Date(0);
    return {
      top: ORDER_TYPES[status] ?? '',
      topColor: STATUS_COLORS[status] ?? '#000',
      bottom: log.username
        ? `${formatLocalDate(when)}\n${log.username}`
        : formatLocalDate(when),
      time: when.getTime(),
    };
  });
  if (!order.props?.directDeliveryInStore) {
    for (const [field, label] of [
      ['receivedDateBlois', 'Received at Blois'],
      ['shipmentDateBlois', 'Shipped from Blois'],
    ] as const) {
      const value = order[field];
      if (value) {
        const when = new Date(value);
        items.push({
          top: label,
          topColor: '#000',
          bottom: formatLocalDate(when),
          time: when.getTime(),
        });
      }
    }
  }
  const sorted = sortByKey(items, (item) => item.time ?? 0);
  const last = sorted.at(-1);
  if (last) last.active = true;
  const lastStatus = Number(part.statusLogs?.at(-1)?.currentStatusId ?? 0);
  const upcoming = Object.keys(ORDER_TYPES)
    .filter((status) => compareStatus(status, lastStatus) > 0)
    .sort(compareStatus)
    .filter((status) => Number(status) !== CS.CANCELLED)
    .map((status) => ({
      top: ORDER_TYPES[Number(status)] ?? '',
      topColor: INACTIVE_COLOR,
      bottom: '',
    }));
  return [...sorted, ...upcoming];
};
export const measureImageUrl = (
  part: OrderPart,
  measurement: Measurement,
): string | null =>
  getImage(
    'dms',
    STORAGE.DmsFrontAssets,
    `/configurator/measures/${part.selected.measureType || 'DMS'}_${(measurement.letter ?? '').toUpperCase()}_${measurement.name}.png`,
  );
export const measurementOutOfLimit = (measurement: Measurement): boolean => {
  if (measurement.min == null) return false;
  const value = Number(measurement.value);
  return measurement.min > value || (measurement.max ?? Infinity) < value;
};

export const anyMeasurementOutOfLimit = (part: OrderPart): boolean =>
  (part.measurements ?? []).some(measurementOutOfLimit);

interface FabricSelection {
  data?: { code?: string | null } | null;
}

/** A fabric typed by hand rather than picked. */
export const hasSpecialFabric = (part: OrderPart): boolean => {
  const fabric = part.selected.FABRIC as FabricSelection | string | null;
  return !fabric || typeof fabric === 'string' || !fabric.data?.code;
};
export const fabricCode = (part: OrderPart): string | undefined => {
  if (hasSpecialFabric(part)) {
    const fabric = part.selected.FABRIC;
    return typeof fabric === 'string' ? fabric : undefined;
  }
  return (part.selected.FABRIC as FabricSelection).data?.code ?? undefined;
};
export const isUnknownFabricCode = (
  code: string | undefined,
  knownCodes: ReadonlySet<string> | undefined,
): boolean => !!code && !!knownCodes && !knownCodes.has(code);

/** The sizes of the model, or anything. */
export const partSizePattern = (
  part: OrderPart,
  modelSizes: ReadonlyMap<string, string> | undefined,
): RegExp => {
  const sizes = modelSizes?.get(String(part.model.data.model ?? ''));
  const list = sizes ? sizes.split(',') : [];
  if (list.length === 0) return /.*/;
  return new RegExp(`^(${list.map(escapeRegExp).join('|')})$`);
};

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);

/** The upper-case keys of `selected` but OPTION and FABRIC. */
export const partElements = (
  part: OrderPart,
): { label: string; value: string }[] =>
  Object.keys(part.selected)
    .filter(
      (key) =>
        key.toUpperCase() === key && key !== 'OPTION' && key !== 'FABRIC',
    )
    .map((key) => ({
      label: key,
      value: String(
        (part.selected[key] as { data?: { name?: unknown } } | undefined)?.data
          ?.name ?? '',
      ),
    }));

const OPTION_KEYS = [
  { key: 'sleevesFinition', dynamic: true },
  { key: 'buttoningTab', dynamic: false },
  { key: 'buttoningTabNoButton', dynamic: false },
  { key: 'bastingPoints', dynamic: false },
] as const;

/** The picked options plus the flags stored on `selected`. */
export const partOptions = (
  part: OrderPart,
  t: (key: string) => string,
): string[] => [
  ...(part.selected.OPTION ?? []).map((option) => option.data?.name ?? ''),
  ...OPTION_KEYS.filter((option) => !!part.selected[option.key]).map(
    (option) =>
      option.dynamic
        ? t(`DMS_configurator.${String(part.selected[option.key])}`)
        : t(`DMS_configurator.${option.key}`),
  ),
];

const EMBROIDERY_LABELS: Readonly<Record<string, string>> = {
  'GOTHIQ CD': 'CD GOTHIC',
  CD: 'INITIALS (ABC)',
  BEE: 'CD BEE',
};
export const embroideryLabel = (type: string): string =>
  EMBROIDERY_LABELS[type] ?? type;

/** `+3`, `-2`, `0`, empty when missing. */
export const withSign = (value: unknown): string => {
  if (value == null) return '';
  return `${Number(value) > 0 ? '+' : ''}${String(value)}`;
};

/** Older orders stored one-letter initials without their dot. */
export const fixWithDots = (
  text: string | null | undefined,
  withDots: unknown,
): string => {
  const value = text ?? '';
  return withDots === true && value.length === 1 ? `${value}.` : value;
};

/** The dark picture of the model, or the configurator default. */
export const modelImageUrl = (part: OrderPart): string | null => {
  const picture = part.model.data.darkPicture;
  let platform: 'abc' | 'dms' = 'dms';
  let storage: StorageName | undefined = STORAGE.DmsFrontAssets;
  let path = picture ?? '';
  if (picture?.includes(STORAGE.AbcDiorBack)) {
    platform = 'abc';
    storage = undefined;
  } else if (picture?.includes(STORAGE.DmsFrontAssets)) {
    storage = undefined;
  } else {
    path = `/configurator/model/${part.model.data.model ?? ''}_BLACK.png`;
  }
  return getImage(platform, storage, path);
};

/** The PDF type and file name of a group of parts. */
export const purchaseOrderFile = (
  order: Order,
  group: readonly OrderPart[],
): {
  type: 'shirt' | 'other';
  uploadType: 'SHIRT' | 'OTHER';
  fileName: string;
} => {
  const shirts = withoutCancelled(group).some(isShirt);
  return shirts
    ? {
        type: 'shirt',
        uploadType: 'SHIRT',
        fileName: `MTM_201650_PDF_SHIRT_${order.displayId ?? ''}.pdf`,
      }
    : {
        type: 'other',
        uploadType: 'OTHER',
        fileName: `MTM_062045_PDF_SUITING_${order.displayId ?? ''}.pdf`,
      };
};

/** The delivery date turns orange under ten days. */
export const deliveryDateSoon = (
  value: string | null | undefined,
  now = Date.now(),
): boolean => {
  if (!value) return false;
  const days = (new Date(value).getTime() - now) / 86_400_000;
  return days < 10;
};

/** Who checked a validation field, and when (`M/d/yyyy H:mm:ss`). */
export const validationInfo = (
  checked: { user?: unknown; date?: unknown } | null | undefined,
  users:
    readonly { id: string; username?: string | null | undefined }[] | undefined,
): string => {
  if (!checked) return '';
  const date = checked.date ? new Date(String(checked.date)) : null;
  const stamp = date
    ? `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`
    : '';
  const user = users?.find((candidate) => candidate.id === checked.user);
  return user ? `${user.username ?? ''} - ${stamp}` : stamp;
};
